/**
 * -----------------------------------------------------------------------------
 * Account Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of an account of the user.
 */

import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useEditOrganizationAccountMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { useSingleEnterpriseQuery } from "../../../api/network/sysadminApiServices";
import {
  setAccountData,
  setMapStateData,
  setTimezoneForDateFns,
} from "../../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import Accordian from "../../../components/accordian";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import {
  ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE,
  APP_CONFIG,
} from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { TMapState } from "../../../types/map";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserPerms } from "../../../utils/user";
import { AccountFt1AccessPolicyField } from "../ft1Access";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import {
  AccountAssetLabelConfigForm,
  AccountFuelCostForm,
  AccountGeneralDetailForm,
  AccountMaintenanceIntervalLabelForm,
  AccountMapSettingsForm,
  AccountReportingOperationalForm,
  AccountScorecardWeightFactorsForm,
  AccountUnitPreferenceForm,
} from "./account-form";
import {
  accountDetailsInitialValues,
  accountDetailsYupValidationSchema,
} from "./validation";

const ScreenAdminDetailAccount = () => {
  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState(false);

  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );

  // get account details from session storage
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    getLocalStorageItem(localStorageKeys.user) ?? loggedInUserData?.user;
  const { account } = loggedInUser;

  // hooks
  const { convertToTZForDateFns } = useAccountTimezone();
  const { getUserACLAccessLevel } = useACL();
  const { getLoggedInUserDashboardUrl } = useLoggedInUserPerms();

  // account mutations
  const [
    editOrganizationAccountApiTrigger,
    { isLoading: isLoadingEditAccount },
  ] = useEditOrganizationAccountMutation();

  // fetch single enterprise details
  const {
    data: dataSingleEnterprise,
    isFetching: isFetchingSingleEnterprise,
    isError: isErrorSingleEnterprise,
    error: errorSingleEnterprise,
  } = useSingleEnterpriseQuery(
    {
      enterprise_id: `${loggedInUser.accountid}`,
    },
    {
      skip: !loggedInUser.accountid,
      refetchOnMountOrArgChange: true,
    }
  );

  const [modifiedCheckBoxData, setModifiedCheckBoxData] = useState<
    Record<string, boolean>
  >({});
  const getUpdatedMaintenanceFlags = () => {
    const maintenanceFlagPayload = {
      maint_flag_km0: modifiedCheckBoxData?.last_maintenance_1,
      maint_flag_km1: modifiedCheckBoxData?.last_maintenance_2,
      maint_flag_km2: modifiedCheckBoxData?.last_maintenance_3,
      maint_flag_km3: modifiedCheckBoxData?.last_maintenance_4,
      maint_flag_km4: modifiedCheckBoxData?.last_maintenance_5,
      maint_flag_km5: modifiedCheckBoxData?.last_maintenance_6,
      maint_flag_km6: modifiedCheckBoxData?.last_maintenance_7,
      maint_flag_km7: modifiedCheckBoxData?.last_maintenance_8,
      maint_flag_km8: modifiedCheckBoxData?.last_maintenance_9,
      maint_flag_km9: modifiedCheckBoxData?.last_maintenance_10,
      maint_flag_hr0: modifiedCheckBoxData?.last_eng_hours_maint_1,
      maint_flag_hr1: modifiedCheckBoxData?.last_eng_hours_maint_2,
      maint_flag_hr2: modifiedCheckBoxData?.last_eng_hours_maint_3,
      maint_flag_hr3: modifiedCheckBoxData?.last_eng_hours_maint_4,
      maint_flag_hr4: modifiedCheckBoxData?.last_eng_hours_maint_5,
      maint_flag_ft0: modifiedCheckBoxData?.last_service_time_1,
      maint_flag_ft1: modifiedCheckBoxData?.last_service_time_2,
      maint_flag_ft2: modifiedCheckBoxData?.last_service_time_3,
      maint_flag_ft3: modifiedCheckBoxData?.last_service_time_4,
      maint_flag_ft4: modifiedCheckBoxData?.last_service_time_5,
    };

    return {
      maintenance_flag: maintenanceFlagPayload,
    };
  };
  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: accountDetailsInitialValues,
    validationSchema: accountDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const data = {
        accountid: values.account_id,
        description: values.account_description,
        contactname: values.contact_name,
        contactphone: values.contact_phone_number,
        contactemail: values.contact_email,
        privategascost: values.private_cost,
        timezone: values.timezone,
        speedunits: values.speed_units,
        distanceunits: values.distance_units,
        volumeunits: values.volume_units,
        economyunits: values.economy_units,
        pressureunits: values.pressure_units,
        temperatureunits: values.temperature_units,
        cameraliveviewduration:values.cameraliveviewduration,
        minidleseconds: values.minidleseconds * 60, // convert minutes to seconds
        device_title: values.device_title, // TODO: [P] check with BE
        device_title_plural: values.device_title_plural, // TODO: [P] check with BE
        device_group_title: values.device_group_title, // TODO: [P] check with BE
        device_group_title_plural: values.device_group_title_plural, // TODO: [P] check with BE
        address_title: values.address_title, // TODO: [P] check with BE
        maintlabelkm0: values.last_maintenance_1,
        maintlabelkm1: values.last_maintenance_2,
        maintlabelkm2: values.last_maintenance_3,
        maintlabelkm3: values.last_maintenance_4,
        maintlabelkm4: values.last_maintenance_5,
        maintlabelkm5: values.last_maintenance_6,
        maintlabelkm6: values.last_maintenance_7,
        maintlabelkm7: values.last_maintenance_8,
        maintlabelkm8: values.last_maintenance_9,
        maintlabelkm9: values.last_maintenance_10,
        maintlabelhr0: values.last_eng_hours_maint_1,
        maintlabelhr1: values.last_eng_hours_maint_2,
        maintlabelhr2: values.last_eng_hours_maint_3,
        maintlabelhr3: values.last_eng_hours_maint_4,
        maintlabelhr4: values.last_eng_hours_maint_5,
        maintlabelft0: values.last_service_time_1,
        maintlabelft1: values.last_service_time_2,
        maintlabelft2: values.last_service_time_3,
        maintlabelft3: values.last_service_time_4,
        maintlabelft4: values.last_service_time_5,
        scorewtharshbrake: values.harsh_braking,
        scorewtharshaccel: values.harsh_acceleration,
        scorewtspeeding: values.speeding,
        scorewtreverse: values.reverse,
        scorewtseatbeltoff: values.seatbelt_off,
        scorewtcornering: values.harsh_cornering, // TODO: or scorewtcorneringai ?
        scorewtidle: values.idle_ratio,
        scorewtimpactai: values.impact_crash_ai,
        scorewtphoneuseai: values.cellphone_use_ai,
        scorewtdistractedai: values.distracted_driving_ai,
        scorewtfooddrinkai: values.drinking_eating_ai,
        scorewtsmokingai: values.smoking_ai,
        scorewtfatigueai: values.possible_fatiuge_ai,
        scorewtobjcameraai: values.obstructed_camera_ai,
        scorewttailgatingai: values.tailgating_ai,
        scorewtfrontcollisionai: values.scorewtfrontcollisionai,
        scorewtpedestriancollisionai: values.scorewtpedestriancollisionai,
        scorewtlanedepartureai: values.scorewtlanedepartureai,
        scorewtstopandgoai: values.scorewtstopandgoai,
        scorewtrollingstopai: values.scorewtrollingstopai,
        scorewtcamerapowerlossai: values.scorewtcamerapowerlossai,
        scorewtcameracoverremovalai: values.scorewtcameracoverremovalai,
        scorewt75for30ai: values.scorewt75for30ai,
        requiretwofactor: values.use_2fa ? 1 : 0,
        infoboxalwaysopen: values.infobox_always_open ? 1 : 0,
        infoboxassetid: values.infobox_asset_id ? 1 : 0,
        infoboxassetdesc: values.infobox_asset_desc ? 1 : 0,
        infoboxassetdisplay: values.infobox_asset_display ? 1 : 0,
        infoboxdriver: values.infobox_driver ? 1 : 0,
        // toggleft: values.toggleft ? 1 : 0, // only sysadmin has access to this field

        // maintenance flags / checkboxes
        ...getUpdatedMaintenanceFlags(),

        // asset label configuration - convert array to comma separated string for BE
        asset_label_fields: values.asset_label_fields.join(","),
        asset_searchable_fields: values.asset_searchable_fields.join(","),
      };

      // call api for account update
      editOrganizationAccountApiTrigger({
        account_id: values.account_id,
        data,
      })
        .unwrap()
        .then((updatedAccount) => {
          toast.success(t("toast.account_updated"));
          if (!!updatedAccount) {
            // the response wins wherever it says anything, what we just
            // sent fills in the fields it stayed silent about, and the old
            // cached account covers the rest.
            const mergedAccount = {
              ...(account ?? {}),
              ...data,
              ...updatedAccount,
            };

            // update session storage with updated account data
            const user: TLegacyUserWithAccount = { ...loggedInUser };
            user.account = mergedAccount;
            setLocalStorageItem(localStorageKeys.user, user);
            setLocalStorageItem(
              localStorageKeys.ownerOrganization,
              mergedAccount
            );

            // update account data in redux store
            dispatch(setAccountData(mergedAccount));

            // update timezone in redux store
            const timezoneForDateFns = convertToTZForDateFns(
              mergedAccount.timezone
            );
            dispatch(setTimezoneForDateFns(timezoneForDateFns));

            // also update the redux store especially map clustering
            dispatch(
              setMapStateData({
                // ...mapState,
                mapLayerOptions: {
                  ...mapState.mapLayerOptions,
                  clustering: mergedAccount.enableclustering === 1, // for values 0 and 2, disable clustering
                },
              })
            );
          }
          navigate(getLoggedInUserDashboardUrl());
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });
  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    formik;

  // pre-fill formik values
  useEffect(() => {
    if (account) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        account_id: account.accountid,
        account_description: account.description ?? "",
        contact_name: account.contactname ?? "",
        contact_phone_number: account.contactphone ?? "",
        contact_email: account.contactemail, // loggedInUser.contactemail ?? "",
        private_cost: account.privategascost ?? 0.0,
        timezone: account.timezone ?? "",
        speed_units: `${account.speedunits}`,
        distance_units: `${account.distanceunits}`,
        volume_units: `${account.volumeunits}`,
        economy_units: `${account.economyunits}`,
        pressure_units: `${account.pressureunits}`,
        temperature_units: `${account.temperatureunits}`,
        cameraliveviewduration: account.cameraliveviewduration,
        minidleseconds: account.minidleseconds ? Math.floor(Number(account.minidleseconds) / 60) : 0, //convert seconds to minutes
        device_title: "", // TODO: check with BE and client team
        device_title_plural: "", // TODO: check with BE and client team
        device_group_title: "", // TODO: check with BE and client team
        device_group_title_plural: "", // TODO: check with BE and client team
        address_title: "", // TODO: check with BE and client team
        last_maintenance_1: `${account.maintlabelkm0}`,
        last_maintenance_2: `${account.maintlabelkm1}`,
        last_maintenance_3: `${account.maintlabelkm2}`,
        last_maintenance_4: `${account.maintlabelkm3}`,
        last_maintenance_5: `${account.maintlabelkm4}`,
        last_maintenance_6: `${account.maintlabelkm5}`,
        last_maintenance_7: `${account.maintlabelkm6}`,
        last_maintenance_8: `${account.maintlabelkm7}`,
        last_maintenance_9: `${account.maintlabelkm8}`,
        last_maintenance_10: `${account.maintlabelkm9}`,
        last_eng_hours_maint_1: `${account.maintlabelhr0}`,
        last_eng_hours_maint_2: `${account.maintlabelhr1}`,
        last_eng_hours_maint_3: `${account.maintlabelhr2}`,
        last_eng_hours_maint_4: `${account.maintlabelhr3}`,
        last_eng_hours_maint_5: `${account.maintlabelhr4}`,
        last_service_time_1: `${account.maintlabelft0}`,
        last_service_time_2: `${account.maintlabelft1}`,
        last_service_time_3: `${account.maintlabelft2}`,
        last_service_time_4: `${account.maintlabelft3}`,
        last_service_time_5: `${account.maintlabelft4}`,
        harsh_braking: `${account.scorewtharshbrake}`,
        harsh_acceleration: `${account.scorewtharshaccel}`,
        speeding: `${account.scorewtspeeding}`,
        reverse: `${account.scorewtreverse}`,
        seatbelt_off: `${account.scorewtseatbeltoff}`,
        harsh_cornering: `${account.scorewtcornering}`, // TODO: or scorewtcorneringai ?
        idle_ratio: `${account.scorewtidle}`,
        impact_crash_ai: `${account.scorewtimpactai}`,
        cellphone_use_ai: `${account.scorewtphoneuseai}`,
        distracted_driving_ai: `${account.scorewtdistractedai}`,
        drinking_eating_ai: `${account.scorewtfooddrinkai}`,
        smoking_ai: `${account.scorewtsmokingai}`,
        possible_fatiuge_ai: `${account.scorewtfatigueai}`,
        obstructed_camera_ai: `${account.scorewtobjcameraai}`,
        tailgating_ai: `${account.scorewttailgatingai}`,
        scorewtfrontcollisionai: `${account.scorewtfrontcollisionai}`,
        scorewtpedestriancollisionai: `${account.scorewtpedestriancollisionai}`,
        scorewtlanedepartureai: `${account.scorewtlanedepartureai}`,
        scorewtstopandgoai: `${account.scorewtstopandgoai}`,
        scorewtrollingstopai: `${account.scorewtrollingstopai}`,
        scorewtcamerapowerlossai: `${account.scorewtcamerapowerlossai}`,
        scorewtcameracoverremovalai: `${account.scorewtcameracoverremovalai}`,
        scorewt75for30ai: `${account.scorewt75for30ai}`,
        // toggleft: account.toggleft === 1 ? true : false, // only sysadmin has access to this field
        use_2fa: account.requiretwofactor === 1 ? true : false,
        infobox_always_open: account.infoboxalwaysopen === 1 ? true : false,
        // read the same way the maps read it 
        infobox_asset_id: account.infoboxassetid === 1,
        infobox_asset_desc: account.infoboxassetdesc === 1,
        infobox_asset_display: account.infoboxassetdisplay === 1,
        infobox_driver: account.infoboxdriver === 1,
        asset_label_fields: account.asset_label_fields
          ? account.asset_label_fields.split(",")
          : APP_CONFIG.ASSET_CONFIGS.DEFAULT_LABEL_CONFIG, // assuming asset_label_fields is a comma separated string from BE
        asset_searchable_fields: account.asset_searchable_fields
          ? account.asset_searchable_fields.split(",")
          : APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS.map(
              (option) => option.value
            ), // assuming asset_searchable_fields is a comma separated string from BE
      });
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle edit information
  const handleEditInformation = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={() => navigate(getLoggedInUserDashboardUrl())}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center font-semibold text-accent-blue-dark text-lg leading-6">
          {t("sub_heading")}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-grow"></div>
          {userCanEdit && !!Object.keys(errors).length && (
            <FillAllRequiredFieldsCaption />
          )}
          {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ACCOUNT) >=
            ROLE_ACCESS_LEVELS.WRITE && (
            <div className="w-24">
              <AdminFormFieldSubmit
                type="button"
                variant="primary"
                label={userCanEdit ? tMain("update") : tMain("edit")}
                onClick={
                  userCanEdit
                    ? handleEditInformation
                    : () => setUserCanEdit(!userCanEdit)
                }
                disabled={
                  isLoadingEditAccount ||
                  (userCanEdit && !!Object.keys(errors).length)
                }
              />
            </div>
          )}
        </div>
        <div className="rounded-lg mt-2 bg-accent-blue-pale">
          <form onSubmit={handleSubmit}>
            <Accordian title={t("accord_account_details")} openByDefault={true}>
              <AccountGeneralDetailForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
            <Accordian title={t("accord_map_setting")} openByDefault={false}>
              <AccountMapSettingsForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
            <Accordian title={t("accord_reporting")} openByDefault={false}>
              <AccountReportingOperationalForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
            <Accordian title={t("accord_unit_preferences")} openByDefault={false}>
              <AccountUnitPreferenceForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
            <Accordian title={t("accord_fuel_and_cost")} openByDefault={false}>
              <AccountFuelCostForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>

            <Accordian title={t("accord_maintenance_interval_labels")}>
              <AccountMaintenanceIntervalLabelForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
                maintenanceFlag={dataSingleEnterprise?.maintenance_flag}
                setModifiedCheckBoxData={setModifiedCheckBoxData}
              />
            </Accordian>
            <Accordian title={t("accord_scorecard_weight_factors")}>
              <AccountScorecardWeightFactorsForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
            <Accordian
              title={
                t("accord_asset_label_fields") +
                ` ${APP_CONFIG.DEBUG.ALL ? `(Label Set: ${!!account.asset_label_fields ? "YES" : "NO"}, Search Set: ${!!account.asset_searchable_fields ? "YES" : "NO"})` : ""}`
              }
            >
              <AccountAssetLabelConfigForm
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formik.setFieldValue}
                handleBlur={handleBlur}
                formikSetTouched={formik.setFieldTouched}
                formikValidateForm={formik.validateForm}
                userCanEdit={userCanEdit}
                loadingData={!formikValuesReady}
              />
            </Accordian>
          </form>
          {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) >=
            ROLE_ACCESS_LEVELS.ALL && (
            <Accordian title={`9. ${tMain("ft1_access.accord_title")}`}>
              <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
                <AccountFt1AccessPolicyField
                  accountId={`${account.accountid}`}
                  userCanEdit={userCanEdit}
                />
              </div>
            </Accordian>
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenAdminDetailAccount;
